<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

type Placement =
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end";

type PanelRole = "dialog" | "menu";

const props = withDefaults(
  defineProps<{
    placement?: Placement;
    role?: PanelRole;
    dismissOnOutsideClick?: boolean;
    dismissOnEsc?: boolean;
    returnFocus?: boolean;
    transition?: string;
    panelClass?: string;
    ariaLabel?: string;
    // Render the panel into <body> with fixed positioning so it escapes any `overflow` ancestor that
    // would otherwise clip it (e.g. inside a scrollable card). Off by default — inline absolute panel.
    teleport?: boolean;
    // Tailwind z-index utility for the panel. Defaults to `z-dropdown` (the standard menu/popover
    // layer). Raise it (e.g. `z-modal`) when the panel must float above a page-level overlay such as
    // the non-modal AI Agent window, which sits at `z-overlay`.
    panelZ?: string;
    // Teleport-only: pin the panel to the trigger's width (used for select/typeahead dropdowns that
    // should line up with their input). Ignored when not teleported, where the panel is absolute and
    // callers size it with `panelClass` (e.g. `w-full`) instead.
    matchTriggerWidth?: boolean;
    // Driven by a guide step that is teaching this menu's own options (see welcomeFlow.store.ts's
    // heldOpenMenu): the popover opens itself and ignores its own trigger, outside clicks and Escape
    // until the step moves on, and its panel paints above the tour's dim instead of under it.
    heldOpen?: boolean;
  }>(),
  {
    placement: "bottom-end",
    role: "dialog",
    dismissOnOutsideClick: true,
    dismissOnEsc: true,
    returnFocus: true,
    transition: "fade-top-quick",
    panelClass: "",
    ariaLabel: undefined,
    teleport: false,
    panelZ: "z-dropdown",
    matchTriggerWidth: false,
    heldOpen: false,
  }
);

const emit = defineEmits<{
  open: [];
  close: [];
}>();

const isOpen = ref(false);
const containerRef = ref<HTMLDivElement | null>(null);
const panelRef = ref<HTMLDivElement | null>(null);
const previouslyFocusedElement = ref<HTMLElement | null>(null);

// When teleported to <body>, CSS classes can't position the panel relative to the trigger, so we
// anchor it with fixed coordinates derived from the trigger's rect (mirrors placementClass). GAP
// matches the 8px (mb-2 / mt-2 / mr-2 / ml-2) offset the inline placement classes use.
const GAP = 8;
const panelStyle = ref<Record<string, string>>({});
// Re-position when the panel's own size changes (e.g. async content like EPD results loads and grows it).
let resizeObserver: ResizeObserver | null = null;

const computePosition = () => {
  if (!props.teleport || !containerRef.value || !panelRef.value) return;
  const trigger = containerRef.value.getBoundingClientRect();
  const panel = panelRef.value.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 8;
  const alignStart = props.placement.endsWith("-start");
  // Width used for horizontal placement/clamping: the trigger's width when pinned, else the panel's
  // own measured (content-driven) width.
  const width = props.matchTriggerWidth ? trigger.width : panel.width;
  let top: number;
  let left: number;
  if (props.placement.startsWith("top")) {
    top = trigger.top - GAP - panel.height;
    left = alignStart ? trigger.left : trigger.right - width;
  } else if (props.placement.startsWith("bottom")) {
    top = trigger.bottom + GAP;
    left = alignStart ? trigger.left : trigger.right - width;
  } else if (props.placement.startsWith("left")) {
    left = trigger.left - GAP - width;
    top = alignStart ? trigger.top : trigger.bottom - panel.height;
  } else {
    left = trigger.right + GAP;
    top = alignStart ? trigger.top : trigger.bottom - panel.height;
  }
  // Clamp so the panel stays fully on-screen no matter where the trigger sits.
  left = Math.max(margin, Math.min(left, vw - width - margin));
  top = Math.max(margin, Math.min(top, vh - panel.height - margin));
  panelStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    ...(props.matchTriggerWidth ? { width: `${trigger.width}px` } : {}),
  };
};

const onReposition = () => computePosition();

const open = () => {
  if (isOpen.value) return;
  if (props.returnFocus) {
    const active = document.activeElement;
    previouslyFocusedElement.value = active instanceof HTMLElement ? active : null;
  }
  isOpen.value = true;
  emit("open");
};

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit("close");
  if (props.returnFocus && previouslyFocusedElement.value) {
    const target = previouslyFocusedElement.value;
    previouslyFocusedElement.value = null;
    nextTick(() => target.focus());
  }
};

const toggle = () => {
  if (props.heldOpen) return;
  if (isOpen.value) close();
  else open();
};

const handleDocumentClick = (event: MouseEvent) => {
  if (props.heldOpen) return;
  const target = event.target as Node;
  // The panel may be teleported out of the container, so a click inside either counts as "inside".
  if (containerRef.value?.contains(target)) return;
  if (panelRef.value?.contains(target)) return;
  if (target instanceof Element && target.closest(".popover-keep-open")) return;
  close();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (props.heldOpen) return;
  if (event.key === "Escape") {
    event.stopPropagation();
    close();
  }
};

// A guide step holding this menu open drives it directly — opens it immediately, closes it the
// moment the step lets go, independent of any click the held-open guards above are refusing. The
// same attribute WelcomeTour.vue tags its own current anchor with (style.css's
// [data-welcome-tour-anchor] rule) goes on the panel too, so the held-open menu gets the tour's
// highlight ring with no separate wiring — set once the panel exists (v-if="isOpen" renders on the
// next tick), removed before it closes.
watch(
  () => props.heldOpen,
  async (held) => {
    if (held) {
      open();
      await nextTick();
      panelRef.value?.setAttribute("data-welcome-tour-anchor", "");
    } else {
      panelRef.value?.removeAttribute("data-welcome-tour-anchor");
      close();
    }
  },
  { immediate: true }
);

watch(isOpen, (opened) => {
  if (opened) {
    if (props.dismissOnOutsideClick) {
      // Capture phase so the outside-click handler still fires when a child
      // element (e.g. a library card with `event.stopPropagation()`) prevents
      // the click from bubbling to document.
      document.addEventListener("click", handleDocumentClick, true);
    }
    if (props.dismissOnEsc) {
      document.addEventListener("keydown", handleKeydown);
    }
    if (props.teleport) {
      // Measure + place once the panel has rendered, re-clamp whenever its size changes (async
      // content), and keep it pinned to the trigger as ancestors scroll or the window resizes.
      nextTick(() => {
        computePosition();
        if (panelRef.value && typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => computePosition());
          resizeObserver.observe(panelRef.value);
        }
      });
      window.addEventListener("scroll", onReposition, true);
      window.addEventListener("resize", onReposition);
    }
  } else {
    document.removeEventListener("click", handleDocumentClick, true);
    document.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("scroll", onReposition, true);
    window.removeEventListener("resize", onReposition);
    resizeObserver?.disconnect();
    resizeObserver = null;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick, true);
  document.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("scroll", onReposition, true);
  window.removeEventListener("resize", onReposition);
  resizeObserver?.disconnect();
});

const placementClass = computed(() => {
  switch (props.placement) {
    case "top-start":
      return "bottom-full left-0 mb-2";
    case "top-end":
      return "bottom-full right-0 mb-2";
    case "bottom-start":
      return "top-full left-0 mt-2";
    case "bottom-end":
      return "top-full right-0 mt-2";
    case "left-start":
      return "right-full top-0 mr-2";
    case "left-end":
      return "right-full bottom-0 mr-2";
    case "right-start":
      return "left-full top-0 ml-2";
    case "right-end":
      return "left-full bottom-0 ml-2";
    default:
      return "top-full right-0 mt-2";
  }
});

// Held open by a guide step: paints above the tour's dim (z-tour) instead of the ordinary panelZ.
const effectivePanelZ = computed(() => (props.heldOpen ? "z-tour" : props.panelZ));
</script>

<template>
  <div
    ref="containerRef"
    class="relative">
    <slot
      name="trigger"
      :toggle="toggle"
      :open="open"
      :close="close"
      :is-open="isOpen" />

    <Teleport
      to="body"
      :disabled="!teleport">
      <Transition :name="transition">
        <div
          v-if="isOpen"
          ref="panelRef"
          :role="role"
          :aria-label="ariaLabel"
          :class="[
            effectivePanelZ,
            'rounded-8 bg-elevated ring-1 ring-slate-200',
            teleport ? 'fixed' : ['absolute', placementClass],
            panelClass,
          ]"
          :style="teleport ? panelStyle : undefined">
          <slot
            :toggle="toggle"
            :open="open"
            :close="close"
            :is-open="isOpen" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
